import { Box } from "@mui/material";
import { type KeyboardEvent, useCallback } from "react";

import { ShiftStatusFilter, type ShiftStatusFilterType } from "../types";

interface GuidedSupportTabsProps {
  readonly statusFilter: ShiftStatusFilterType;
  readonly onStatusFilterChange: (status: ShiftStatusFilterType) => void;
  readonly upcomingCount?: number;
  readonly pastCount?: number;
}

/**
 * Custom tab component for filtering shifts by upcoming or past status.
 * Styled to match Figma design with full-width pills and inline counts.
 *
 * Accessibility features:
 * - Proper ARIA roles (tablist, tab)
 * - Keyboard navigation (arrow keys to switch tabs)
 * - Screen reader support for counts
 */
export function GuidedSupportTabs(props: GuidedSupportTabsProps) {
  const { statusFilter, onStatusFilterChange, upcomingCount = 0, pastCount = 0 } = props;

  const isPastSelected = statusFilter === ShiftStatusFilter.PAST;
  const isUpcomingSelected = statusFilter === ShiftStatusFilter.UPCOMING;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        const newStatus = isPastSelected ? ShiftStatusFilter.UPCOMING : ShiftStatusFilter.PAST;
        onStatusFilterChange(newStatus);
      }
    },
    [isPastSelected, onStatusFilterChange]
  );

  return (
    <Box
      role="tablist"
      aria-label="Filter shifts by status"
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        borderRadius: "100px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        width: "100%",
        marginY: theme.spacing(4),
      })}
      onKeyDown={handleKeyDown}
    >
      <TabButton
        count={pastCount}
        isSelected={isPastSelected}
        label="Past shifts"
        onClick={() => {
          onStatusFilterChange(ShiftStatusFilter.PAST);
        }}
      />
      <TabButton
        count={upcomingCount}
        isSelected={isUpcomingSelected}
        label="Upcoming shifts"
        onClick={() => {
          onStatusFilterChange(ShiftStatusFilter.UPCOMING);
        }}
      />
    </Box>
  );
}

interface TabButtonProps {
  readonly label: string;
  readonly count: number;
  readonly isSelected: boolean;
  readonly onClick: () => void;
}

function TabButton(props: TabButtonProps) {
  const { label, count, isSelected, onClick } = props;

  // Screen reader accessible label including count
  const accessibleLabel = `${label} (${count})`;

  return (
    <Box
      component="button"
      type="button"
      role="tab"
      aria-selected={isSelected}
      aria-label={accessibleLabel}
      tabIndex={isSelected ? 0 : -1}
      sx={(theme) => ({
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(2),
        height: theme.spacing(10),
        minWidth: 0,
        paddingX: theme.spacing(5),
        paddingY: theme.spacing(4),
        borderRadius: theme.borderRadius?.xLarge,
        border: "none",
        cursor: "pointer",
        backgroundColor: isSelected ? theme.palette.primary.main : theme.palette.background.paper,
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: isSelected ? theme.palette.primary.dark : theme.palette.action.hover,
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: "2px",
        },
      })}
      onClick={onClick}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={(theme) => ({
          ...theme.typography.body2,
          fontWeight: theme.typography.fontWeightMedium,
          color: isSelected ? theme.palette.common.white : theme.palette.text.primary,
          whiteSpace: "nowrap",
        })}
      >
        {label}
        {/* Non-breaking space for visual separation */}
        &nbsp;
        <Box
          component="span"
          sx={(theme) => ({
            fontWeight: theme.typography.fontWeightRegular,
            color: isSelected ? theme.palette.primary.contrastText : theme.palette.text.secondary,
          })}
        >
          {count}
        </Box>
      </Box>
    </Box>
  );
}
